import { useEffect, useRef, useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../context/AuthContext';
import UpgradeModal from '../common/UpgradeModal';

export default function ClientForm({ editingClient, onEditComplete }) {
    const { addClient, updateClient, clients } = useAppContext();
    const { userProfile } = useAuth();
    const { canAddClient, getClientLimit } = usePermissions();

    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [address, setAddress] = useState('');
    const [neighborhood, setNeighborhood] = useState('');
    const [city, setCity] = useState('');
    const [reference, setReference] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    const nameRef = useRef(null);

    useEffect(() => {
        if (nameRef.current) nameRef.current.focus();
    }, []);

    // Carregar dados do cliente para edição
    useEffect(() => {
        if (editingClient) {
            setName(editingClient.name || '');
            setPhone(editingClient.phone || '');
            setEmail(editingClient.email || '');
            setAddress(editingClient.address || '');
            setNeighborhood(editingClient.neighborhood || '');
            setCity(editingClient.city || '');
            setReference(editingClient.reference || '');
            setNotes(editingClient.notes || '');
        }
    }, [editingClient]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (!name.trim()) return;

        // Verificar limite de clientes apenas para novos cadastros
        if (!editingClient && !canAddClient(clients.length)) {
            setShowUpgradeModal(true);
            return;
        }

        try {
            setSaving(true);
            const clientData = {
                name: name.trim(),
                phone: phone.trim(),
                email: email.trim(),
                address: address.trim(),
                neighborhood: neighborhood.trim(),
                city: city.trim(),
                reference: reference.trim(),
                notes: notes.trim(),
            };

            if (editingClient) {
                await updateClient(editingClient.id, clientData);
                setSuccess('✓ Cliente atualizado!');
                if (onEditComplete) onEditComplete();
            } else {
                await addClient(clientData);
                setSuccess('✓ Cliente cadastrado!');
            }

            // Limpar form se não estiver editando
            if (!editingClient) {
                setName('');
                setPhone('');
                setEmail('');
                setAddress('');
                setNeighborhood('');
                setCity('');
                setReference('');
                setNotes('');
                if (nameRef.current) nameRef.current.focus();
            }

            setTimeout(() => setSuccess(''), 2500);
        } catch (err) {
            setError('Não foi possível salvar o cliente. Tente novamente.');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (onEditComplete) onEditComplete();
        setName('');
        setPhone('');
        setEmail('');
        setAddress('');
        setNeighborhood('');
        setCity('');
        setReference('');
        setNotes('');
    };

    const currentPlan = userProfile?.plan || 'basico';
    const clientLimit = getClientLimit();
    const isNearLimit = currentPlan === 'basico' && clients.length >= clientLimit * 0.8;
    const atLimit = currentPlan === 'basico' && clients.length >= clientLimit;

    return (
        <>
            <form onSubmit={handleSubmit} className="card shadow-sm">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <h2 className="h5 mb-0">
                            {editingClient ? '✏️ Editar Cliente' : '👤 Novo Cliente'}
                        </h2>
                        {!editingClient && currentPlan === 'basico' && (
                            <span className={`badge ${atLimit ? 'bg-danger' : isNearLimit ? 'bg-warning' : 'bg-info'}`}>
                                {clients.length}/{clientLimit} clientes
                            </span>
                        )}
                    </div>

                    {!editingClient && isNearLimit && !atLimit && (
                        <div className="alert alert-warning py-2 small mb-3">
                            <i className="bi bi-exclamation-triangle-fill me-1"></i>
                            Você está próximo do limite do plano Básico ({clients.length}/{clientLimit}).
                            Considere fazer upgrade para clientes ilimitados.
                        </div>
                    )}

                    {success && <div className="alert alert-success py-2 small">{success}</div>}
                    {error && <div className="alert alert-danger py-2 small">{error}</div>}

                <div className="mb-3">
                    <label className="form-label fw-semibold">Nome completo *</label>
                    <input
                        ref={nameRef}
                        type="text"
                        className="form-control"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Ex: João Silva"
                        required
                    />
                </div>

                <div className="row">
                    <div className="col-6 mb-3">
                        <label className="form-label fw-semibold">Telefone</label>
                        <input
                            type="tel"
                            className="form-control"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="(00) 00000-0000"
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label fw-semibold">E-mail</label>
                        <input
                            type="email"
                            className="form-control"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="email@exemplo.com"
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label className="form-label fw-semibold">Endereço de entrega</label>
                    <input
                        type="text"
                        className="form-control"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Rua, número, complemento"
                    />
                </div>

                <div className="row">
                    <div className="col-6 mb-3">
                        <label className="form-label fw-semibold">Bairro</label>
                        <input
                            type="text"
                            className="form-control"
                            value={neighborhood}
                            onChange={(e) => setNeighborhood(e.target.value)}
                            placeholder="Ex: Centro"
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label fw-semibold">Cidade</label>
                        <input
                            type="text"
                            className="form-control"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            placeholder="Ex: São Paulo"
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label className="form-label fw-semibold">Ponto de referência</label>
                    <input
                        type="text"
                        className="form-control"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        placeholder="Ex: Próximo ao mercado"
                    />
                </div>

                <div className="mb-3">
                    <label className="form-label fw-semibold">Observações</label>
                    <textarea
                        className="form-control"
                        rows="2"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Anotações adicionais sobre o cliente"
                    />
                </div>

                <div className="d-flex gap-2">
                    <button
                        type="submit"
                        className="btn btn-primary flex-grow-1"
                        disabled={saving}
                    >
                        {saving ? '⏳ Salvando...' : editingClient ? '✓ Atualizar' : '✓ Cadastrar'}
                    </button>
                    {editingClient && (
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCancel}
                        >
                            Cancelar
                        </button>
                    )}
                </div>
            </div>
        </form>

        <UpgradeModal
            show={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            feature="clients"
        />
        </>
    );
}
